import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import {
  getDefaultFiscalYearStartDate,
  suggestFiscalYearEndDate,
  fiscalYearLengthInMonths,
  MAX_FISCAL_YEAR_MONTHS,
} from '@/services/onboardingService';

interface CompanyContextFormData {
  companyType: string;
  fiscalYear: string;
  fiscalYearStartDate: string;
  fiscalYearEndDate: string;
  accountingMethod: string;
  description: string;
  vatRegistered: string;
  employerFee: string;
  fTax: string;
}

interface CompanyContextFormProps {
  onSave: (
    contextText: string,
    fiscalYear: string,
    fiscalYearStartDate: string,
    fiscalYearEndDate: string,
    accountingMethod: string,
    companyType: string,
    vatRegistered: boolean,
    fSkatt: boolean,
  ) => Promise<void>;
  isLoading: boolean;
}

// The values below are what the form stores and sends (a Swedish-language protocol the AI context
// and the backend read); only the labels shown on screen come from the message catalog.
const COMPANY_TYPES = [
  { value: 'Enskild firma (EF)', dbValue: 'ef' },
  { value: 'Aktiebolag (AB)', dbValue: 'ab' },
  { value: 'Handelsbolag (HB)', dbValue: 'hb' },
  { value: 'Kommanditbolag (KB)', dbValue: 'kb' },
  { value: 'Ekonomisk förening', dbValue: 'ekonomisk_forening' },
  { value: 'Annat', dbValue: 'annat' },
] as const;

const FISCAL_YEARS = [
  { value: 'Kalenderår (jan–dec)', labelKey: 'calendar' },
  { value: 'Apr–mar', labelKey: 'aprMar' },
  { value: 'Jul–jun', labelKey: 'julJun' },
  { value: 'Okt–sep', labelKey: 'octSep' },
] as const;

const ACCOUNTING_METHODS = [
  { value: 'Fakturametoden', labelKey: 'invoice' },
  { value: 'Kontantmetoden', labelKey: 'cash' },
] as const;

const YES = 'Ja';
const NO = 'Nej';

const EMPTY_FORM: CompanyContextFormData = {
  companyType: '',
  fiscalYear: '',
  // broken-fiscal-year.AC9: today is the default, via the one named, swappable function —
  // not re-derived here, so a future registration-date lookup only changes that one function.
  fiscalYearStartDate: getDefaultFiscalYearStartDate(),
  fiscalYearEndDate: '',
  accountingMethod: '',
  description: '',
  vatRegistered: '',
  employerFee: '',
  fTax: '',
};

function serializeToMarkdown(data: CompanyContextFormData): string {
  return [
    '## Firmauppgifter',
    '',
    `**Bolagsform:** ${data.companyType}`,
    `**Räkenskapsår:** ${data.fiscalYear}`,
    `**Bokföringsmetod:** ${data.accountingMethod}`,
    `**Verksamhet:** ${data.description}`,
    `**Momsregistrerad:** ${data.vatRegistered}`,
    `**Registrerad för arbetsgivaravgift:** ${data.employerFee}`,
    // F-skatt is NOT included here — it's persisted as a real column
    // (companies.f_skatt) below, not free text, same reasoning as vatRegistered.
  ].join('\n');
}

export function CompanyContextForm({ onSave, isLoading }: CompanyContextFormProps) {
  const t = useTranslations('consultantOnboarding.companyContext.form');
  const [form, setForm] = useState<CompanyContextFormData>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof CompanyContextFormData) => (value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // broken-fiscal-year.F1/S1 Option A: a normal cadence (the dropdown) plus the company's actual
  // start date — the end date is re-suggested whenever either changes (so it always matches what's
  // currently selected, never a stale suggestion from an earlier start date), but stays a plain,
  // freely-editable field: a manual override is the user's last action, never followed by another
  // period/start change in the same sitting.
  const handleFiscalYearPeriodChange = (period: string) => {
    setForm(prev => ({ ...prev, fiscalYear: period, fiscalYearEndDate: suggestFiscalYearEndDate(prev.fiscalYearStartDate, period) }));
  };

  const handleStartDateChange = (value: string) => {
    setForm(prev => ({ ...prev, fiscalYearStartDate: value, fiscalYearEndDate: suggestFiscalYearEndDate(value, prev.fiscalYear) }));
  };

  const fiscalYearLengthMessage = (() => {
    if (!form.fiscalYearStartDate || !form.fiscalYearEndDate) return null;
    const months = fiscalYearLengthInMonths(form.fiscalYearStartDate, form.fiscalYearEndDate);
    if (months === null) return null;
    if (months > MAX_FISCAL_YEAR_MONTHS) {
      return { text: t('fiscalYearTooLong', { months, max: MAX_FISCAL_YEAR_MONTHS }), isError: true };
    }
    if (months !== 12) {
      return { text: t('fiscalYearUnusual', { months }), isError: false };
    }
    return null;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const missing = Object.entries(form)
      .filter(([, v]) => !v.trim())
      .map(([k]) => k);

    if (missing.length > 0) {
      setError(t('missing'));
      return;
    }

    // "Momsregistrerad?" and "Registrerad för F-skatt?" already existed here (asked at
    // onboarding) but were only ever serialized into free-text markdown — issue #287 needs
    // them as real booleans on companies.vat_registered/f_skatt (a legitimately
    // "not VAT-registered" seller is a valid state, not missing data — see the
    // invoice-generation mandatory-field gate).
    const companyType = COMPANY_TYPES.find(type => type.value === form.companyType);
    await onSave(
      serializeToMarkdown(form), form.fiscalYear, form.fiscalYearStartDate, form.fiscalYearEndDate,
      form.accountingMethod, companyType?.dbValue ?? '', form.vatRegistered === YES, form.fTax === YES,
    );
  };

  const yesNoQuestions: { key: 'vatRegistered' | 'employerFee' | 'fTax'; label: string }[] = [
    { key: 'vatRegistered', label: t('vatRegistered') },
    { key: 'employerFee', label: t('employerFee') },
    { key: 'fTax', label: t('fTax') },
  ];
  const yesNoOptions = [
    { value: YES, label: t('yes') },
    { value: NO, label: t('no') },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="companyType">{t('companyType')} *</Label>
        <Select onValueChange={set('companyType')} value={form.companyType} disabled={isLoading}>
          <SelectTrigger id="companyType">
            <SelectValue placeholder={t('companyTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {COMPANY_TYPES.map(type => (
              <SelectItem key={type.dbValue} value={type.value}>{t(`types.${type.dbValue}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="fiscalYear">{t('fiscalYear')} *</Label>
        <Select onValueChange={handleFiscalYearPeriodChange} value={form.fiscalYear} disabled={isLoading}>
          <SelectTrigger id="fiscalYear">
            <SelectValue placeholder={t('fiscalYearPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {FISCAL_YEARS.map(year => (
              <SelectItem key={year.labelKey} value={year.value}>{t(`fiscalYears.${year.labelKey}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t('fiscalYearHint')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fiscalYearStartDate">{t('fiscalYearStartDate')} *</Label>
          <Input
            id="fiscalYearStartDate"
            type="date"
            value={form.fiscalYearStartDate}
            onChange={e => handleStartDateChange(e.target.value)}
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fiscalYearEndDate">{t('fiscalYearEndDate')} *</Label>
          <Input
            id="fiscalYearEndDate"
            type="date"
            value={form.fiscalYearEndDate}
            onChange={e => set('fiscalYearEndDate')(e.target.value)}
            disabled={isLoading}
          />
        </div>
      </div>
      {fiscalYearLengthMessage && (
        <Alert variant={fiscalYearLengthMessage.isError ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{fiscalYearLengthMessage.text}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="accountingMethod">{t('accountingMethod')} *</Label>
        <Select onValueChange={set('accountingMethod')} value={form.accountingMethod} disabled={isLoading}>
          <SelectTrigger id="accountingMethod">
            <SelectValue placeholder={t('accountingMethodPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {ACCOUNTING_METHODS.map(method => (
              <SelectItem key={method.labelKey} value={method.value}>{t(`methods.${method.labelKey}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">{t('description')} *</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={e => set('description')(e.target.value)}
          placeholder={t('descriptionPlaceholder')}
          rows={3}
          disabled={isLoading}
        />
      </div>

      {yesNoQuestions.map(({ key, label }) => (
        <fieldset key={key} className="space-y-2 border-0 p-0 m-0">
          <legend className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label} *
          </legend>
          <div className="flex gap-4">
            {yesNoOptions.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={key}
                  value={opt.value}
                  checked={form[key] === opt.value}
                  onChange={() => set(key)(opt.value)}
                  disabled={isLoading}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? t('saving') : t('submit')}
      </Button>
    </form>
  );
}
